/**
 * TableExplorerPagination Component
 *
 * Pagination controls for Table Explorer with page navigation, row count display,
 * and page size selector.
 */

import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { GridStatusInline, type GridStatusPhase } from '../../common/components/GridStatusInline';
import { ESTIMATED_TOTAL_TITLE } from '../utils/paginationModel';
import type { PopulationProgressEntry } from '../../query-editor/hooks/usePopulationProgress';

/**
 * Page size options for table data fetching. Must include every default limit
 * an open action can use, else the selector renders blank — e.g. the object
 * explorer's "Edit top 200 rows" opens Table Explorer at `limit: 200`.
 */
export const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: '500', label: '500' },
  { value: '1000', label: '1,000' },
  { value: '10000', label: '10,000' },
];

type TableExplorerPaginationProps = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRowCount: number;
  /**
   * True when no exact total is available — the engine skipped the full-table
   * count (Kusto). Next/Prev are then driven by `hasNextPage` (full-page
   * has-more detection) and the numbered/First/Last controls are hidden.
   */
  totalUnknown?: boolean;
  /**
   * True when `totalRowCount` is a catalog-statistics ESTIMATE rather than a
   * `COUNT(*)` — the exact count missed its settle budget on an unfiltered
   * fetch. The row range then renders the total with a "≈" prefix and explains
   * itself on hover; page navigation is otherwise identical (the has-more guard
   * in `derivePaginationModel` covers an under-estimate).
   */
  totalIsEstimate?: boolean;
  /** Whether a Next page should be offered (see `totalUnknown`). */
  hasNextPage?: boolean;
  /** Cursor-backed hosts can disable Previous when that page has been evicted. */
  hasPreviousPage?: boolean;
  /** Refetch page one instead of reconstructing an opaque continuation handle. */
  onFirstPage?: () => void;
  previousUnavailableReason?: string;
  /** 1-based first row shown, precomputed by derivePaginationModel. */
  rangeStart?: number;
  /** 1-based last row shown, precomputed by derivePaginationModel. */
  rangeEnd?: number;
  rangeMode?: 'global' | 'loaded';
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  isLoading?: boolean;
  /** True while a TanStack Query fetch is in flight (covers both initial load and refetch). */
  isFetching?: boolean;
  /** Streaming progress for the in-flight fetch. Drives the populating/loading X of Y display. */
  populationProgress?: PopulationProgressEntry;
  /**
   * Cancel the in-flight fetch. When omitted, no Stop button is shown. Called
   * again on a second press while `isCancelling` — that call is the user's
   * escalation to a force-stop.
   */
  onCancelFetch?: () => void;
  /**
   * True after `onCancelFetch` is invoked; flips the Stop button to "Stopping…"
   * without disabling it, so the escalation press stays available.
   */
  isCancelling?: boolean;
  zoomLevel?: number;
  onZoomChange?: (zoom: number) => void;
};

/**
 * Format a number with thousand separators
 */
function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Calculate the row range for display (e.g., "Rows 101-200")
 */
function getRowRange(currentPage: number, pageSize: number, totalRowCount: number): { start: number; end: number } {
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalRowCount);
  return { start, end };
}

export function TableExplorerPagination({
  currentPage,
  totalPages,
  pageSize,
  totalRowCount,
  totalUnknown = false,
  totalIsEstimate = false,
  hasNextPage,
  hasPreviousPage,
  onFirstPage,
  previousUnavailableReason,
  rangeStart,
  rangeEnd,
  rangeMode = 'global',
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  isLoading = false,
  isFetching = false,
  populationProgress,
  onCancelFetch,
  isCancelling = false,
  zoomLevel = 1,
  onZoomChange,
}: TableExplorerPaginationProps) {
  const resolvedPageSizeOptions = pageSizeOptions
    ? pageSizeOptions.map((value) => ({ value: String(value), label: formatNumber(value) }))
    : PAGE_SIZE_OPTIONS;
  const fallbackRange = getRowRange(currentPage, pageSize, totalRowCount);
  const start = rangeStart ?? fallbackRange.start;
  const end = rangeEnd ?? fallbackRange.end;

  // Derive the status phase from streaming progress + fetching state.
  // - `populating` while batches are arriving (drives "Loading… X of Y rows")
  // - `awaiting`   while the SQL is in flight but nothing has streamed yet
  // - `idle`       once the fetch settles — falls through to the static row-count display
  const phase: GridStatusPhase =
    populationProgress?.state === 'populating'
      ? {
          kind: 'populating',
          loadedRowCount: populationProgress.loadedRowCount,
          finalRowCount: populationProgress.finalRowCount,
        }
      : isFetching
        ? { kind: 'awaiting' }
        : { kind: 'idle' };

  // Pagination nav is locked during any non-idle phase to avoid request thrash.
  const navDisabled = isLoading || isFetching || phase.kind !== 'idle';
  const canGoBack = (hasPreviousPage ?? currentPage > 1) && !navDisabled;
  // First is never live on page 1 — there is nothing to restart from (S4 review
  // F8). Past page 1 it stays live even when a cursor host's Previous is gone.
  // Without `hasPreviousPage` this equals `canGoBack`, the base First rule.
  const canGoFirst = currentPage > 1 && !navDisabled;
  // When the total is unknown, `hasNextPage` (full-page has-more) is the only
  // signal; otherwise the exact page count decides. `hasNextPage` already
  // encodes `currentPage < totalPages` for the known case, so it wins whenever
  // it's supplied.
  const canGoForward = (hasNextPage ?? currentPage < totalPages) && !navDisabled;
  // Show the nav cluster whenever movement is possible. With a known total that
  // means >1 page; with an unknown total, whenever we can step back or forward.
  const showNav = totalUnknown ? currentPage > 1 || !!hasNextPage : totalPages > 1;

  // Generate page numbers to display (show at most 5 pages centered on current)
  const getVisiblePages = (): number[] => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: number[] = [];
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    // Adjust if we're near the end
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/30 text-xs select-none">
      {/* Left side: Row count and page size selector */}
      <div className="flex items-center gap-4">
        {/* Row count info. STAYS MOUNTED during a fetch (dimmed) instead of
            being swapped for the status line: the swap changed this slot's
            width, which shoved the page-size selector and page numbers
            sideways on every page change (user-reported). The live status
            renders on the RIGHT, next to the zoom controls, where nothing
            interactive sits to its left. During the first load (no rows yet)
            there is no meaningful range — the slot renders nothing and the
            right-side status is the only feedback. */}
        {(phase.kind === 'idle' || totalRowCount > 0) && (
          <div
            className={`text-muted-foreground whitespace-nowrap tabular-nums select-text ${phase.kind !== 'idle' ? 'opacity-50' : ''}`}
            data-testid="te-row-range"
            {...(totalIsEstimate ? { title: ESTIMATED_TOTAL_TITLE, 'data-estimated': 'true' } : {})}
          >
            {rangeMode === 'loaded' ? (
              <>{formatNumber(end)} row{end !== 1 ? 's' : ''} on this page</>
            ) : totalUnknown ? (
              // Unknown total (count skipped) — show the loaded range only, with
              // no "of N". A short/empty page renders "0 rows".
              end > 0 ? (
                <>
                  {formatNumber(start)}-{formatNumber(end)}
                </>
              ) : (
                <>0 rows</>
              )
            ) : totalRowCount > pageSize ? (
              <>
                {formatNumber(start)}-{formatNumber(end)} of {totalIsEstimate ? '≈' : ''}
                {formatNumber(totalRowCount)}
              </>
            ) : (
              <>
                {totalIsEstimate ? '≈' : ''}
                {formatNumber(totalRowCount)} row{totalRowCount !== 1 ? 's' : ''}
              </>
            )}
          </div>
        )}

        {/* Page size selector */}
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground whitespace-nowrap">Page size:</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger
                className="h-6 w-[80px] text-xs"
                data-testid="page-size-selector"
                {...(pageSizeOptions ? { 'data-page-size-options': resolvedPageSizeOptions.map((option) => option.value).join(',') } : {})}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {resolvedPageSizeOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-xs"
                    data-testid={`page-size-option-${option.value}`}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {previousUnavailableReason && (
          <span className="text-muted-foreground" data-testid="pagination-previous-unavailable" role="status">
            {previousUnavailableReason}
          </span>
        )}
      </div>

      {/* Pagination controls — shown whenever movement is possible. With a
          known total that's >1 page; with an unknown total (count skipped),
          whenever we can step back or forward (has-more). */}
      {showNav && (
        <div className="flex items-center gap-1">
          {/* Cursor-backed hosts supply a dedicated page-one refetch. */}
          {(!totalUnknown || onFirstPage) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onFirstPage ? onFirstPage() : onPageChange(1)}
              disabled={!canGoFirst}
              title="First page"
              data-testid="pagination-first"
            >
              <ChevronFirst className="h-4 w-4" />
            </Button>
          )}

          {/* Previous page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!canGoBack}
            title={!canGoBack && previousUnavailableReason ? previousUnavailableReason : 'Previous page'}
            data-testid="pagination-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page numbers — replaced by a plain "Page N" marker when the total
              is unknown (we can't enumerate pages we can't count). */}
          {totalUnknown ? (
            <span
              className="px-2 text-muted-foreground whitespace-nowrap tabular-nums select-text"
              data-testid="pagination-page-indicator"
            >
              Page {formatNumber(currentPage)}
            </span>
          ) : (
          <div className="flex items-center gap-0.5">
            {visiblePages[0] > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-xs"
                  onClick={() => onPageChange(1)}
                  disabled={navDisabled}
                  data-testid="pagination-page-1"
                >
                  1
                </Button>
                {visiblePages[0] > 2 && (
                  <span className="px-1 text-muted-foreground">...</span>
                )}
              </>
            )}

            {visiblePages.map((page) => (
              <Button
                key={page}
                variant={page === currentPage ? 'default' : 'ghost'}
                size="sm"
                className="h-7 w-7 p-0 text-xs"
                onClick={() => onPageChange(page)}
                disabled={navDisabled}
                data-testid={`pagination-page-${page}`}
              >
                {page}
              </Button>
            ))}

            {visiblePages[visiblePages.length - 1] < totalPages && (
              <>
                {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                  <span className="px-1 text-muted-foreground">...</span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-xs"
                  onClick={() => onPageChange(totalPages)}
                  disabled={navDisabled}
                  data-testid={`pagination-page-${totalPages}`}
                >
                  {totalPages}
                </Button>
              </>
            )}
          </div>
          )}

          {/* Next page */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!canGoForward}
            title="Next page"
            data-testid="pagination-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page — needs a known last page, so hidden when total unknown */}
          {!totalUnknown && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onPageChange(totalPages)}
              disabled={!canGoForward}
              title="Last page"
              data-testid="pagination-last"
            >
              <ChevronLast className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* Right cluster: live fetch status + zoom controls. The status lives
          HERE (not in the row-count slot) so its appearance never displaces
          the page-size selector or the page numbers. */}
      <div className="flex items-center gap-3 ml-auto">
        {phase.kind !== 'idle' && (
          // The strip is chrome (`select-none`), but the live row counts this
          // status renders are values a user copies — opt them back in.
          <span className="inline-flex items-center select-text">
            <GridStatusInline
              phase={phase}
              onCancel={onCancelFetch}
              isCancelling={isCancelling}
              testIdAwaiting="te-status-awaiting"
              testIdPopulating="te-status-populating"
              compact
            />
          </span>
        )}
        {onZoomChange && (
        <div className="flex items-center gap-0.5">
          {zoomLevel !== 1 && (
            <button
              type="button"
              className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/60"
              onClick={() => onZoomChange(1)}
              title={`Reset zoom (${Math.round(zoomLevel * 100)}%)`}
              data-testid="te-zoom-reset"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/60"
            onClick={() => onZoomChange(Math.max(0.5, +(zoomLevel - 0.1).toFixed(1)))}
            title="Zoom out"
            data-testid="te-zoom-out"
          >
            <ZoomOut className="h-3 w-3" />
          </button>
          <button
            type="button"
            className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/60"
            onClick={() => onZoomChange(Math.min(2, +(zoomLevel + 0.1).toFixed(1)))}
            title="Zoom in"
            data-testid="te-zoom-in"
          >
            <ZoomIn className="h-3 w-3" />
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
